<template>
  <div class="container-fluid py-4">
    <h2 class="mb-4 fw-bold">Admin > Hosted Accounts Assignment</h2>

    <TabsContainer
      :tabs="tabs"
      :active-tab="activeTab"
      @change:tab="activeTab = $event as 'matches' | 'certificates'"
    >
      <!-- Suggested Matches Tab -->
      <template #tab-matches>
        <LoadingIndicatorComponent v-if="loadingMatches" />
        <div v-else>
          <!-- Matching normally runs with the 15-minute registry sync, so an explicit
               re-run is what refreshes suggestions after assigning by hand (and is the
               only way to produce them outside production). -->
          <div class="d-flex justify-content-end mb-3">
            <button
              class="btn btn-sm btn-outline-primary"
              @click="handleRematch"
              :disabled="isRematching"
            >
              <font-awesome-icon
                :icon="isRematching ? 'spinner' : 'magic'"
                :spin="isRematching"
                class="me-2"
              />
              {{ isRematching ? "Matching…" : "Re-run matching" }}
            </button>
          </div>
          <SuggestedMatchesTable
            :matches="matches"
            :pendingCoverage="pendingCoverage"
            @match-confirmed="handleMatchUpdated"
            @match-rejected="handleMatchUpdated"
          />
        </div>
      </template>

      <!-- Unassigned Certificates Tab -->
      <template #tab-certificates>
        <LoadingIndicatorComponent v-if="loadingCertificates" />
        <div v-else>
          <UnknownCertificatesTable
            :certificates="unknownCertificates"
            v-model="selectedCertificateIds"
            @assign-click="handleAssignClick"
          />
        </div>
      </template>
    </TabsContainer>

    <!-- Assign Modal -->
    <AssignCertificatesModal
      v-if="showAssignModal"
      :selected-certificates="selectedCertificatesForModal"
      :is-visible="showAssignModal"
      @assigned="handleAssigned"
      @close="showAssignModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import TabsContainer from "@/components/common/tabs/TabsContainer.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { Tab } from "@/composables/useTabs";
import { computed, onMounted, ref } from "vue";
import AssignCertificatesModal from "./AssignCertificatesModal.vue";
import SuggestedMatchesTable from "./SuggestedMatchesTable.vue";
import UnknownCertificatesTable from "./UnknownCertificatesTable.vue";
import type {
  MatchResponse,
  PendingOrderCoverage,
  UnknownCertificateResponse,
} from "./virtualPortfoliosApi";
import { virtualPortfoliosApi } from "./virtualPortfoliosApi";

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const activeTab = ref<"matches" | "certificates">("matches");
const matches = ref<MatchResponse[]>([]);
const pendingCoverage = ref<PendingOrderCoverage[]>([]);
const unknownCertificates = ref<UnknownCertificateResponse[]>([]);
const selectedCertificateIds = ref<number[]>([]);
const loadingMatches = ref(false);
const loadingCertificates = ref(false);
const showAssignModal = ref(false);
const isRematching = ref(false);

const tabs = computed<Tab[]>(() => [
  {
    id: "matches",
    // Counts the filling orders too, so the tab does not read as empty while orders
    // are waiting for volume — the tab is the only hint they exist.
    name: `Suggested Matches${
      matches.value.length + pendingCoverage.value.length > 0
        ? ` (${matches.value.length}${
            pendingCoverage.value.length > 0
              ? ` + ${pendingCoverage.value.length} filling`
              : ""
          })`
        : ""
    }`,
    icon: "magic",
  },
  {
    id: "certificates",
    name: `Unassigned Certificates${
      unknownCertificates.value.length > 0
        ? ` (${unknownCertificates.value.length})`
        : ""
    }`,
    icon: "file-alt",
  },
]);

const selectedCertificatesForModal = computed(() => {
  return unknownCertificates.value.filter((cert) =>
    selectedCertificateIds.value.includes(cert.id)
  );
});

async function loadMatches() {
  try {
    loadingMatches.value = true;
    const response = await virtualPortfoliosApi.getMatches();
    matches.value = response.data.matches;
    pendingCoverage.value = response.data.pending_coverage ?? [];
  } catch (error) {
    // Cleared rather than left in place: keeping the previous counts would show a tab
    // label and rows that no longer reflect anything that was successfully loaded.
    matches.value = [];
    pendingCoverage.value = [];
    globalErrorHandler(error);
  } finally {
    loadingMatches.value = false;
  }
}

async function loadCertificates() {
  try {
    loadingCertificates.value = true;
    const response = await virtualPortfoliosApi.getUnknownCertificates();
    unknownCertificates.value = response.data.certificates;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    loadingCertificates.value = false;
  }
}

function handleAssignClick(certificateIds: number[]) {
  selectedCertificateIds.value = certificateIds;
  showAssignModal.value = true;
}

async function handleRematch() {
  if (isRematching.value) return;

  try {
    isRematching.value = true;
    const { data } = await virtualPortfoliosApi.rematch();
    globalSuccessHandler(
      "Matching complete",
      `${data.matched_orders} order(s) matched (${data.matched_mwh.toLocaleString()} MWh), ` +
        `${data.still_filling_orders} still filling`
    );
    await loadMatches();
    await loadCertificates();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isRematching.value = false;
  }
}

async function handleMatchUpdated() {
  await loadMatches();
  await loadCertificates();
}

async function handleAssigned() {
  showAssignModal.value = false;
  selectedCertificateIds.value = [];
  await loadCertificates();
  // Also reload matches in case any were affected
  await loadMatches();
}

onMounted(async () => {
  await loadMatches();
  // Orders still filling are worth landing on too — jumping straight to the
  // certificates tab would hide the fact that matching is in progress.
  if (matches.value.length === 0 && pendingCoverage.value.length === 0) {
    activeTab.value = "certificates";
  }
  loadCertificates();
});
</script>

<style scoped lang="scss"></style>
